import React, { createContext, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AuthContext = createContext();

const extractFirstErrorMessage = (error) => {
  const responseData = error?.response?.data;
  const fieldErrors = responseData?.errors;

  if (fieldErrors && typeof fieldErrors === 'object') {
    const firstEntry = Object.entries(fieldErrors)[0];
    if (firstEntry) {
      const [, value] = firstEntry;
      if (Array.isArray(value) && value.length > 0) {
        return value[0];
      }
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
  }

  return responseData?.message;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // 토큰에서 사용자 정보 가져오기
  const { data: userData, isLoading: userLoading } = useQuery(
    'user',
    () => api.get('/auth/me'),
    {
      retry: false,
      enabled: !!localStorage.getItem('token'), // 토큰이 있을 때만 실행
      onError: (error) => {
        console.error('사용자 정보 조회 실패:', error); // 디버깅용
        // 에러 메시지를 표시하지 않고 조용히 처리
        localStorage.removeItem('token');
        setUser(null);
      },
      onSuccess: (data) => {
        console.log('사용자 정보 조회 성공:', data); // 디버깅용
        setUser(data);
      }
    }
  );

  // 로그인 뮤테이션
  const loginMutation = useMutation(
    (credentials) => api.post('/auth/login', credentials),
    {
      onSuccess: (data) => {
        console.log('로그인 성공:', data); // 디버깅용
        console.log('must_change_password 값:', data.must_change_password); // 디버깅용

        // 초기 비밀번호 변경이 필요한 경우 (Super Admin 제외)
        if (data.must_change_password && data.user?.role !== 'super_admin') {
          console.log('초기 비밀번호 변경 필요:', data.user_id);
          // 초기 비밀번호 변경 모달을 표시하기 위해 상태 설정
          setUser({ ...data, must_change_password: true, user_id: data.user_id });
          return;
        }

        // 토큰과 사용자 정보 저장
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);

        // 성공 메시지 표시 (한 번만)
        toast.success('로그인되었습니다.');

        // 디버깅 정보 표시
        console.log('전체 사용자 데이터:', data.user);
        console.log('사용자 역할:', data.user.role);
        console.log('사용자 타입:', data.user.user_type);
        console.log('토큰 저장됨:', !!data.access_token);

        // React Router를 사용한 리다이렉트
        console.log('리다이렉트 시작...');
        setTimeout(() => {
          if (data.user.role === 'super_admin') {
            console.log('총괄관리자로 리다이렉트'); // 디버깅용
            navigate('/super-admin');
          } else if (data.user.role === 'property_owner') {
            console.log('임대사업자로 리다이렉트'); // 디버깅용
            navigate('/property-owner-dashboard');
          } else if (data.user.role === 'rental_management_company' || 
                     data.user.role === 'rmc_admin' || 
                     data.user.role === 'rmc_staff') {
            console.log('임대관리회사로 리다이렉트'); // 디버깅용
            navigate('/rmc-dashboard');
          } else if (data.user.role === 'admin') {
            console.log('관리자로 리다이렉트'); // 디버깅용
            navigate('/');
          } else {
            console.log('기본 사용자로 리다이렉트'); // 디버깅용
            navigate('/');
          }
        }, 100); // 약간의 지연을 두어 상태 업데이트 완료 후 리다이렉트
      },
      onError: (error) => {
        console.error('로그인 에러:', error); // 디버깅용
        toast.error(error.response?.data?.message || '로그인에 실패했습니다.');
      }
    }
  );

  // 회원가입 뮤테이션
  const registerMutation = useMutation(
    (userData) => api.post('/auth/register', userData),
    {
      onSuccess: () => {
        toast.success('회원가입이 완료되었습니다. 관리자 승인을 기다려주세요.');
        navigate('/login');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '회원가입에 실패했습니다.');
      }
    }
  );

  // 로그아웃
  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    queryClient.clear();
    toast.success('로그아웃되었습니다.');
    navigate('/login');
  };

  // 비밀번호 변경
  const changePasswordMutation = useMutation(
    (passwordData) => api.put('/auth/change-password', passwordData),
    {
      onSuccess: () => {
        toast.success('비밀번호가 변경되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '비밀번호 변경에 실패했습니다.');
      }
    }
  );

  // 초기 비밀번호 변경
  const changeInitialPasswordMutation = useMutation(
    (passwordData) => api.post('/auth/change-initial-password', passwordData),
    {
      onSuccess: () => {
        toast.success('초기 비밀번호가 변경되었습니다. 다시 로그인해주세요.');
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '초기 비밀번호 변경에 실패했습니다.');
      }
    }
  );

  // 프로필 업데이트
  const updateProfileMutation = useMutation(
    (profileData) => api.put('/auth/profile', profileData),
    {
      onSuccess: (data) => {
        setUser(data);
        queryClient.invalidateQueries('user');
        toast.success('프로필이 업데이트되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '프로필 업데이트에 실패했습니다.');
      }
    }
  );

  // 초기 로딩 상태 설정
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
    }
  }, [userLoading]);

  useEffect(() => {
    if (!userLoading) {
      setLoading(false);
    }
  }, [userLoading]);

  const value = {
    user,
    loading,
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout,
    changePassword: changePasswordMutation.mutate,
    changeInitialPassword: changeInitialPasswordMutation.mutate,
    updateProfile: updateProfileMutation.mutate,
    isLoggingIn: loginMutation.isLoading,
    isRegistering: registerMutation.isLoading,
    isChangingPassword: changePasswordMutation.isLoading,
    isChangingInitialPassword: changeInitialPasswordMutation.isLoading,
    isUpdatingProfile: updateProfileMutation.isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
